import React, {useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import {useSearchParams} from 'react-router-dom';
import {ReadableSizeHelper} from '../../Helpers/readableSize';
import {DBService} from '../../Types/service';
import {useGetDatabase} from '../../Hooks/DatabaseManager';
import {useNavigate, createSearchParams} from 'react-router-dom';
import {PageTitle} from '../../../../_metronic/layout/core';
import {useIntl} from 'react-intl';
import IconeService from '../IconeService';
import {useGetMe} from '../../../../app/hooks/useGetMe';
import {isClientCIS} from '../../../Auth/Utils/CustomerHelper';
import ComingSoon from '../../../../app/common/components/ComingSoon';
import EtatBadge from '../EtatBadge';

interface StateOption {
  value: number;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const CheckboxOption = <T extends {label: string; count?: number}>(props: OptionProps<T, true>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        className="me-2 mt-1 flex-shrink-0"
      />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
      {props.data.count != null && (
        <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
      )}
    </div>
  </components.Option>
);

const Database = (): JSX.Element => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ReadableSizeHelperInstance = useMemo(() => new ReadableSizeHelper(), []);

  const buId: string = searchParams.get('buId') ?? '';
  const CISID: string = searchParams.get('CISID') ?? '';
  const customer = useGetMe();

  const {isLoading, data} = useGetDatabase(buId);

  const [selectedStates, setSelectedStates] = useState<StateOption[]>([]);

  const stateOptions = useMemo<StateOption[]>(() => {
    if (!data) return [];
    const map = new Map<number, StateOption>();
    data.forEach((db) => {
      const existing = map.get(db.active);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = db.active
        ? intl.formatMessage({id: 'COMMON.STATUS.ACTIVE'})
        : intl.formatMessage({id: 'COMMON.STATUS.INACTIVE'});
      map.set(db.active, {value: db.active, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [data, intl]);

  const filteredData = useMemo(() => {
    if (!data) return data;
    if (selectedStates.length === 0) return data;
    const states = new Set(selectedStates.map((o) => o.value));
    return data.filter((db) => states.has(db.active));
  }, [data, selectedStates]);

  const goToReclamation = () => {
    searchParams.append(
      'TemplateReclamation',
      'Demande_de_creation_d_une_nouvelle_base_de_donnees'
    );
    searchParams.append('ServiceId', CISID);
    navigate({
      pathname: `/support`,
      search: createSearchParams(searchParams).toString(),
    });
  };

  const renderDatabaseAction = (): JSX.Element => (
    <IconeService
      lien="https://espace-clients-v1.celeste.fr/services/etat-data-base?t=4xRSP5%2BZsxZgRToD%2B%2Fp8A3gnSwYnv0rxCUaAkrLsaCBbs1PBTXLJVrOpg9FHT7UlVkWYAtpCCWA0LpI9mExZxoYC6jkhiCCTMYkgatoRZVg%3D"
      ariaLabel="Administration des bases de données"
      iconClass="fs-1 fa fa-database"
      title="Administration des bases de données"
      targetLink="_blank"
    />
  );

  const columns = useMemo<ColumnDef<DBService>[]>(() => {
    return [
      {
        header: 'Bases de données',
        accessorKey: 'name',
        size: 300,
      },
      {
        header: 'Etat',
        accessorKey: 'active',
        size: 85,
        cell: (info) => {
          const row = info.row.original;

          const color = row.active ? '#5da170' : '#710080';
          const label = row.active
            ? intl.formatMessage({id: 'COMMON.STATUS.ACTIVE'})
            : intl.formatMessage({id: 'COMMON.STATUS.INACTIVE'});
          return <EtatBadge color={color} label={label} />;
        },
      },
      {
        header: 'Stockage utilisé',
        accessorKey: 'quota_usage',
        size: 150,
        cell: (info) => {
          const row = info.row.original;

          return (
            <>
              <span>
                {ReadableSizeHelperInstance.readableSize(row.quota_usage * 1000, 0, 'decimal')}
              </span>
            </>
          );
        },
      },
      {
        header: 'Actions',
        accessorKey: 'actions',
        size: 70,
        cell: () => renderDatabaseAction(),
      },
    ];
  }, [ReadableSizeHelperInstance, intl]);

  const renderMobileCard = (row: DBService): JSX.Element => {
    const color = row.active ? '#5da170' : '#710080';
    const label = row.active
      ? intl.formatMessage({id: 'COMMON.STATUS.ACTIVE'})
      : intl.formatMessage({id: 'COMMON.STATUS.INACTIVE'});

    return (
      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <span className="fw-bold" style={{minWidth: 0, wordBreak: 'break-word'}}>
            {row.name}
          </span>
          <span className="flex-shrink-0">
            <EtatBadge color={color} label={label} />
          </span>
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <span className="text-muted">Stockage utilisé</span>
          <span>
            {ReadableSizeHelperInstance.readableSize(row.quota_usage * 1000, 0, 'decimal')}
          </span>
        </div>
        <div>
          <a
            className="btn btn-sm btn-light-primary"
            href="https://espace-clients-v1.celeste.fr/services/etat-data-base?t=4xRSP5%2BZsxZgRToD%2B%2Fp8A3gnSwYnv0rxCUaAkrLsaCBbs1PBTXLJVrOpg9FHT7UlVkWYAtpCCWA0LpI9mExZxoYC6jkhiCCTMYkgatoRZVg%3D"
            target="_blank"
            rel="noreferrer"
          >
            <i className="fa fa-database me-2" />
            Administration des bases de données
          </a>
        </div>
      </div>
    );
  };

  if (!isClientCIS(customer)) {
    return <ComingSoon title="MENU.CLOUD.GROUP_WEB" />;
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CLOUD.GROUP_WEB'})}</PageTitle>

      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={() => goToReclamation()}>
          <i
            className="fa fa-envelope"
            aria-label="Demander une nouvelle base de données"
            aria-hidden="true"
            title="Demander une nouvelle base de données"
            style={{padding: '0.35rem'}}
          />{' '}
          Demander une nouvelle base de données
        </button>
      </div>

      <TableMetronic<DBService>
        toolbar={
          <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
            <ReactSelect<StateOption, true>
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{Option: CheckboxOption}}
              options={stateOptions}
              value={selectedStates}
              onChange={(vals) => setSelectedStates(vals ? [...vals] : [])}
              placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_STATUS'})}
              noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
              classNamePrefix="filter-select"
              menuPortalTarget={document.body}
              styles={filterSelectStyles}
            />
          </div>
        }
        stackedHeader
        title="Etat des bases de données"
        data={filteredData ?? []}
        columns={columns}
        renderMobileCard={renderMobileCard}
        isLoading={isLoading}
        searchBar
        pagination
      />
    </>
  );
};
export default Database;
